import { NavLink } from "react-router-dom";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";

import { useOrganization } from "@/api/queries";
import { VaultMark } from "@/auth/LoginPage";
import { cn } from "@/lib/cn";
import { NAV_ITEMS } from "./navigation";

/**
 * The persistent desktop sidebar (Frontend Spec §3).
 *
 * Three states, and only CSS decides which one applies:
 *
 * * below `md` — absent. The bottom navigation takes over.
 * * `md` to `desk` — a 56 px icon rail. 240 px of menu out of a 900 px window
 *   is a column of the inventory table, and the table is the work.
 * * `desk` and up — 240 px with labels, unless the user folded it away.
 *
 * `collapsed` is a preference, not a layout decision: it only ever *removes*
 * `desk:` classes. It therefore cannot widen the rail below 1200 px, which is
 * the state a user could not escape — the button that would undo it lives
 * inside the panel doing the damage.
 *
 * Modules that do not exist yet stay listed, greyed, with the phase that will
 * bring them: hiding them would make the product look smaller than it is,
 * pretending they work would be worse.
 */
export function Sidebar({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const { data: organization } = useOrganization();
  /** Only meaningful at `desk` and above; below that the rail always wins. */
  const wide = !collapsed;

  return (
    <aside
      className={cn(
        "hidden shrink-0 flex-col border-r border-border bg-surface transition-[width] duration-150 md:flex",
        "w-14",
        wide && "desk:w-60",
      )}
    >
      <div
        className={cn(
          "flex items-center justify-center gap-2.5 px-2 py-4",
          wide && "desk:justify-start desk:px-4",
        )}
      >
        <VaultMark className="size-9 shrink-0" />
        <div className={cn("min-w-0", wide ? "hidden desk:block" : "hidden")} aria-hidden>
          <p className="text-[15px] font-bold leading-tight tracking-tight text-ink">HD-VAULT</p>
          <p className="truncate text-[10px] uppercase tracking-[0.14em] text-ink-subtle">
            Inventaire · Outils · Projets
          </p>
        </div>
      </div>

      <nav
        className={cn("min-h-0 flex-1 overflow-y-auto px-1.5 pb-4", wide && "desk:px-2")}
        aria-label="Navigation principale"
      >
        <ul className="flex flex-col gap-0.5">
          {NAV_ITEMS.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                title={item.label}
                aria-label={item.label}
                className={({ isActive }) =>
                  cn(
                    "flex items-center justify-center gap-2.5 rounded-md px-0 py-2 text-[13px] transition-colors",
                    wide && "desk:justify-start desk:px-3",
                    isActive
                      ? "bg-brand-subtle font-medium text-brand"
                      : "text-ink-muted hover:bg-surface-hover hover:text-ink",
                    item.comingSoon && "opacity-55",
                  )
                }
              >
                <item.icon className="size-4 shrink-0" aria-hidden />
                {/* aria-hidden: the link already carries the label, so the rail
                    and the wide form announce exactly the same thing. */}
                <span
                  aria-hidden
                  className={cn("truncate", wide ? "hidden desk:inline" : "hidden")}
                >
                  {item.label}
                </span>
                {item.comingSoon && (
                  <span
                    aria-hidden
                    className={cn(
                      "ml-auto shrink-0 whitespace-nowrap rounded border border-border px-1 text-[9px] uppercase tracking-wide text-ink-subtle",
                      wide ? "hidden desk:inline" : "hidden",
                    )}
                  >
                    {item.comingSoon}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Folding away 240 px of menu only means something where there are 240 px
          of menu to fold. Below `desk` the rail is already the narrow form. */}
      <button
        type="button"
        onClick={onToggle}
        aria-label={collapsed ? "Déplier le menu" : "Replier le menu"}
        title={collapsed ? "Déplier le menu" : "Replier le menu"}
        className={cn(
          "hidden items-center gap-2 border-t border-border py-2 text-[12px] text-ink-subtle transition-colors hover:bg-surface-hover hover:text-ink desk:flex",
          collapsed ? "justify-center px-0" : "px-4",
        )}
      >
        {collapsed ? (
          <PanelLeftOpen className="size-4" aria-hidden />
        ) : (
          <>
            <PanelLeftClose className="size-4" aria-hidden />
            Replier le menu
          </>
        )}
      </button>

      {/* The organisation is data, not code: an unnamed installation simply shows
          nothing here rather than someone else's company. */}
      {organization?.name && (
        <div
          className={cn(
            "shrink-0 border-t border-border px-4 py-3",
            wide ? "hidden desk:block" : "hidden",
          )}
        >
          <p className="truncate text-[12px] font-semibold text-ink-muted">{organization.name}</p>
          {organization.tagline && (
            <p className="mt-0.5 truncate text-[10px] uppercase leading-tight tracking-wide text-ink-subtle">
              {organization.tagline}
            </p>
          )}
        </div>
      )}
    </aside>
  );
}
